import React, { useEffect, useState }  from 'react';
import PropTypes from 'prop-types';

import { RichText } from '$partials/global';
import { useLocale } from '$contexts/locale';
import Button from '$partials/ui/button';
import { ModuleStatement } from '$partials/modules/individual';
import { DiamondLogo } from '$partials/icons';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const Section = ({
    title,
    body,
    domain
}) => {
    return (
        <li className='w-1/2 md:w-1/4 col-padding mb-40'>
            <h3 className='type-style-eyebrow uppercase text-text-secondary mb-16'>
                {title}
            </h3>
            <RichText
                className='footer__rich-text'
                domain={domain}
                {...body} />
        </li>
    );
};

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const Footer = ({
    menu,
    domain,
}) => {
    const { translate: t } = useLocale();

    const [hasCookieModal, setHasCookieModal] = useState(false);
    const [hasCookiebot, setHasCookiebot] = useState(false);

    useEffect(() => {
        const check = () => {
            if (typeof window.StravaCookieManagementPlatform?.showDataSharingPreferencesModal === 'function') {
                setHasCookieModal(true);
            }
            if (typeof window.Cookiebot?.show === 'function') {
                setHasCookiebot(true);
            }
        };
        check();
        window.addEventListener('load', check);
        return () => window.removeEventListener('load', check);
    }, []);

    if (!menu) { return null; }
    return (
        <footer className='w-full relative z-10 px-8 pb-8 rounded'>
            <div className={`theme-${menu?.theme} bg-theme-themed-surface-bg rounded-t`}>
                {menu?.animatedStatement &&
                    <ModuleStatement
                        {...menu.animatedStatement}
                        footer={true}
                        theme={menu?.theme || menu?.animatedStatement?.theme} />
                }
                <section className={'layout-container layout-site-outer-padding type-style-10'}>
                    <div className='theme-offset'>
                        <div>

                            {/* Divider */}
                            {menu?.animatedStatement &&
                                <div className='col-padding'>
                                    <div className='border-t border-border-default' />
                                </div>
                            }

                            <div className='pt-40 flex sm:flex-row flex-col'>
                                <div className='w-full sm:w-2/6 md:w-4/12 mb-40 col-padding text-text-secondary'>
                                    <DiamondLogo />
                                    {menu?.copyright &&
                                        <RichText
                                            className='footer__rich-text mt-[43px]'
                                            domain={domain}
                                            {...menu.copyright} />
                                    }
                                    {hasCookieModal &&
                                        <Button
                                            className='footer__rich-text hover:underline mt-15'
                                            onClick={() => {
                                                window.StravaCookieManagementPlatform.showDataSharingPreferencesModal();
                                                return false;
                                            }}>
                                                Do Not Share My Personal Information
                                        </Button>
                                    }
                                </div>

                                <ul className='flex flex-wrap w-full sm:w-4/6 md:w-8/12'>
                                    {menu.column1 &&
                                        <Section
                                            body={menu.column1}
                                            domain={domain}
                                            title={menu.column1Title} />
                                    }
                                    {menu.column2 &&
                                        <Section
                                            body={menu.column2}
                                            domain={domain}
                                            title={menu.column2Title} />
                                    }
                                    {menu.column3 &&
                                        <Section
                                            body={menu.column3}
                                            domain={domain}
                                            title={menu.column3Title} />
                                    }
                                    {menu.column4 &&
                                        <Section
                                            body={menu.column4}
                                            domain={domain}
                                            title={menu.column4Title} />
                                    }
                                </ul>

                            </div>

                            {/* Divider */}
                            <div className='col-padding'>
                                <div className='border-t border-border-default' />
                            </div>

                            <div className='w-full flex flex-col sm:flex-row sm:justify-between col-padding uppercase font-semibold py-20 md:py-30'>
                                {hasCookiebot &&
                                    <button
                                        className='type-style-body-small text-text-secondary self-start mt-15 sm:mt-0 ml-auto'
                                        onClick={() => window.Cookiebot.show()}
                                        type='button'>
                                        {t('MANAGE_COOKIES_BTN_LABEL')}
                                    </button>
                                }
                            </div>

                        </div>
                    </div>
                </section>
            </div>
        </footer>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

Footer.propTypes = {
    domain: PropTypes.string,
    hideBorder: PropTypes.bool,
    menu: PropTypes.shape({
        copyright: PropTypes.object,
        column1: PropTypes.object,
        column2: PropTypes.object,
        column3: PropTypes.object,
        column4: PropTypes.object,
        column1Title: PropTypes.string,
        column2Title: PropTypes.string,
        column3Title: PropTypes.string,
        column4Title: PropTypes.string
    }),
    showNewsletter: PropTypes.bool,
};

Section.propTypes = {
    domain: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ])
};

export default Footer;
